import React, { useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import { Building2, Radio, Droplet, Truck, CheckCircle } from 'lucide-react';

const EmergencyCoordination = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });

  const features = [
    { icon: Building2, text: "AI verified matching", desc: "Instant verification of blood banks and hospitals" },
    { icon: Radio, text: "Geo-ping alerts in seconds", desc: "Real-time location-based emergency alerts" },
    { icon: Droplet, text: "Blood • Medicines • Organs", desc: "Complete emergency resource coordination" },
    { icon: Truck, text: "Multimodal dispatch", desc: "Ambulance • Drone • Air transport" },
    { icon: CheckCircle, text: "Live tracking + compliance", desc: "End-to-end transparency and regulation" }
  ];

  return (
    <section id="emergency" ref={ref} className="py-32 px-4 sm:px-6 lg:px-8 bg-white relative overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div className="grid lg:grid-cols-2 gap-16 items-center">
          {/* Content */}
          <motion.div
            initial={{ opacity: 0, x: -50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6 }}
          >
            <motion.div
              className="inline-block mb-6"
              whileHover={{ scale: 1.05 }}
            >
              <div className="bg-blue-50 border border-blue-100 rounded-full px-5 py-2">
                <span className="text-blue-600 font-semibold text-sm">B2B Solution</span>
              </div>
            </motion.div>
            
            <h2 className="text-3xl sm:text-4xl md:text-5xl lg:text-7xl font-bold mb-6 text-black leading-tight">
              Emergency
              <br />
              <span className="text-blue-600">Coordination</span>
            </h2>
            
            <p className="text-lg sm:text-xl text-gray-600 mb-4 font-medium">
              For Hospitals & Blood Banks
            </p>

            <p className="text-base sm:text-lg text-gray-500 mb-8 sm:mb-12 leading-relaxed">
              Cut response time from 4 hours to 6 minutes with AI-powered geo-ping engine and verified network matching.
            </p>

            {/* Stats Card */}
            <motion.div
              className="bg-black/5 backdrop-blur-xl border border-black/10 rounded-3xl p-6 sm:p-8 mb-8 sm:mb-12"
              whileHover={{ 
                scale: 1.02,
                rotateY: 5,
                z: 50
              }}
              transition={{ type: "spring", stiffness: 300 }}
              style={{ transformStyle: "preserve-3d" }}
            >
              <div className="flex flex-col sm:flex-row items-center justify-between gap-4 sm:gap-0">
                <div className="text-center sm:text-left">
                  <p className="text-sm text-gray-600 font-medium mb-2">Manual Process</p>
                  <p className="text-4xl sm:text-5xl font-bold text-gray-800">4 hrs</p>
                </div>
                <div className="text-3xl sm:text-4xl text-blue-600">→</div>
                <div className="text-center sm:text-right">
                  <p className="text-sm text-gray-600 font-medium mb-2">With SupaAid</p>
                  <p className="text-4xl sm:text-5xl font-bold text-blue-600">6 min</p>
                </div>
              </div>
            </motion.div>
          </motion.div>

          {/* Features Grid */}
          <motion.div
            initial={{ opacity: 0, x: 50 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="space-y-4"
          >
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={isInView ? { opacity: 1, y: 0 } : {}}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  whileHover={{ 
                    scale: 1.03, 
                    x: 10,
                    rotateY: 5,
                    z: 30
                  }}
                  className="bg-white/80 backdrop-blur-xl border border-gray-200 rounded-2xl p-6 shadow-lg hover:shadow-2xl transition-all"
                  style={{ transformStyle: "preserve-3d" }}
                >
                  <div className="flex items-start gap-4">
                    <div className="bg-blue-100 rounded-xl p-3 flex-shrink-0">
                      <Icon className="w-6 h-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="text-lg font-bold text-gray-900 mb-1">{feature.text}</h3>
                      <p className="text-sm text-gray-600">{feature.desc}</p>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default EmergencyCoordination;
